/* THE CHRISTMAS SKY ON HOME (Max, 31/10: "Giao diện Noel tôi thấy chưa wow á", then "bị thô … cần nhiều hiệu ứng wow"). A night sky
   with a moon, twinkling stars and a shooting star, Santa's sleigh crossing it, snowy hills, a shaded tree whose lights twinkle under
   a glowing star, and presents. The words: the greeting in large type and the days left to Christmas / the New Year.
   In the page always and shown only in season by theme-christmas.css (season.js), like the rest.

   Readable first: the words sit on the darkest part of the sky (white on #0b1a33–#123b2b, >= 10:1; check_christmas.py measures
   every stop), the count on its own solid red chip; the drawing is aria-hidden; nothing here moves while the motion switch is off
   or the device asks for reduced motion. */
import React from 'react';
import { useTranslation } from 'react-i18next';
import XmasMusic from './XmasMusic';
import { useSeason } from './season';

/* the day in Vietnam, so a laptop on another clock still counts down to the right morning */
const vnToday = () => { const d = new Date(Date.now() + 7 * 3600000); return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }; };
const daysBetween = (a, b) => Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000);

export const xmasCountdown = (t) => {
  const now = vnToday();
  if (now.m === 12 && now.d === 25) return t('xShell.xmasToday');
  if (now.m === 1) return t('xShell.xmasNewYear', { y: now.y });
  if (now.m === 12 && now.d > 25) return t('xShell.xmasCountNY', { n: daysBetween(now, { y: now.y + 1, m: 1, d: 1 }) });
  return t('xShell.xmasCountXmas', { n: daysBetween(now, { y: now.y, m: 12, d: 25 }) });
};

/* the lights on the tree: [x, y, colour]. They are drawn still in the picture; what twinkles is a glint over each one, an HTML
   element animated by opacity only — animating inside the SVG repainted the whole picture every frame (Max: "bị lag", 31/10) */
const BULBS = [[148, 66, '#fde047'], [172, 70, '#f87171'], [160, 84, '#60a5fa'], [136, 92, '#4ade80'], [184, 96, '#fde047'],
  [124, 112, '#f87171'], [150, 108, '#fde047'], [172, 114, '#4ade80'], [196, 110, '#60a5fa'], [114, 130, '#fde047'],
  [140, 134, '#60a5fa'], [166, 136, '#f87171'], [192, 132, '#fde047'], [208, 128, '#4ade80']];

/* the glints and the star's halo, placed over the picture in its own coordinates (viewBox 330 x 180) */
const Glints = () => (
  <span className="xmas-hero-glints" aria-hidden="true">
    {BULBS.map(([x, y, c], i) => (
      <i key={`${x}-${y}`} style={{ left: `${(x / 330) * 100}%`, top: `${(y / 180) * 100}%`, '--c': c, animationDelay: `${((i * 0.43) % 2.2).toFixed(2)}s` }} />
    ))}
    <b style={{ left: `${(160 / 330) * 100}%`, top: `${(30 / 180) * 100}%` }} />
  </span>
);

const Art = () => (
  <svg className="xmas-hero-art" viewBox="0 0 330 180" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="xh-pine" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2f9e5b" /><stop offset="1" stopColor="#0f4d2c" /></linearGradient>
      <linearGradient id="xh-pine-dark" x1="0" y1="0" x2="1" y2="0"><stop offset="0.5" stopColor="#000" stopOpacity="0" /><stop offset="1" stopColor="#000" stopOpacity=".28" /></linearGradient>
      <linearGradient id="xh-snow" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#ffffff" /><stop offset="1" stopColor="#cfe0f5" /></linearGradient>
      <linearGradient id="xh-snow-back" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#dbe8f7" /><stop offset="1" stopColor="#a9c0dc" /></linearGradient>
      <radialGradient id="xh-glow" cx=".5" cy=".5" r=".5"><stop offset="0" stopColor="#fde68a" stopOpacity=".9" /><stop offset="1" stopColor="#fde68a" stopOpacity="0" /></radialGradient>
      <radialGradient id="xh-red" cx=".35" cy=".3" r=".75"><stop offset="0" stopColor="#fecaca" /><stop offset=".45" stopColor="#ef4444" /><stop offset="1" stopColor="#7f1d1d" /></radialGradient>
      <radialGradient id="xh-gold" cx=".35" cy=".3" r=".75"><stop offset="0" stopColor="#fef9c3" /><stop offset=".45" stopColor="#facc15" /><stop offset="1" stopColor="#854d0e" /></radialGradient>
      <radialGradient id="xh-blue" cx=".35" cy=".3" r=".75"><stop offset="0" stopColor="#dbeafe" /><stop offset=".45" stopColor="#3b82f6" /><stop offset="1" stopColor="#1e3a8a" /></radialGradient>
      <linearGradient id="xh-box-r" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ef4444" /><stop offset="1" stopColor="#991b1b" /></linearGradient>
      <linearGradient id="xh-box-b" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#3b82f6" /><stop offset="1" stopColor="#1e40af" /></linearGradient>
      <linearGradient id="xh-box-g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#22c55e" /><stop offset="1" stopColor="#166534" /></linearGradient>
      <filter id="xh-soft" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="2.2" /></filter>
    </defs>
    {/* hills, back then front */}
    <path d="M0 180 C40 172 72 146 132 144 S248 128 330 118 L330 180 Z" fill="url(#xh-snow-back)" />
    <path d="M14 180 C80 176 112 156 176 157 S296 146 330 142 L330 180 Z" fill="url(#xh-snow)" />
    {/* small pines on the hills */}
    <g opacity=".95"><path d="M40 146 L26 170 L54 170 Z" fill="#14532d" /><path d="M40 132 L30 154 L50 154 Z" fill="#166534" /><path d="M40 122 L33 140 L47 140 Z" fill="#15803d" /></g>
    <g opacity=".9"><path d="M300 132 L284 162 L316 162 Z" fill="#14532d" /><path d="M300 118 L289 142 L311 142 Z" fill="#166534" /><path d="M300 108 L292 126 L308 126 Z" fill="#15803d" /></g>
    {/* the tree: three scalloped tiers, light from the left */}
    <rect x="152" y="146" width="16" height="16" rx="2" fill="#6b3f1d" />
    <path d="M160 92 L98 148 Q114 141 128 148 Q144 140 160 148 Q176 140 192 148 Q206 141 222 148 Z" fill="url(#xh-pine)" />
    <path d="M160 64 L110 116 Q124 110 136 116 Q148 109 160 116 Q172 109 184 116 Q196 110 210 116 Z" fill="url(#xh-pine)" />
    <path d="M160 36 L124 84 Q136 78 146 84 Q153 79 160 84 Q167 79 174 84 Q184 78 196 84 Z" fill="url(#xh-pine)" />
    <path d="M160 36 L196 84 L210 116 L222 148 L160 148 Z" fill="url(#xh-pine-dark)" />
    {/* snow on the branches */}
    <path d="M124 84 Q136 78 146 84 Q153 79 160 84 Q167 79 174 84 Q184 78 196 84 L192 80 Q172 74 160 79 Q146 74 128 80 Z" fill="#f8fafc" opacity=".9" />
    <path d="M110 116 Q124 110 136 116 Q148 109 160 116 Q172 109 184 116 Q196 110 210 116 L204 111 Q182 104 160 110 Q138 104 116 111 Z" fill="#f8fafc" opacity=".85" />
    {/* garlands */}
    <path d="M132 76 Q160 92 190 74" stroke="#facc15" strokeWidth="1.8" fill="none" opacity=".9" />
    <path d="M118 104 Q160 124 204 102" stroke="#facc15" strokeWidth="1.8" fill="none" opacity=".9" />
    <path d="M106 136 Q160 156 216 134" stroke="#facc15" strokeWidth="1.8" fill="none" opacity=".9" />
    {/* baubles */}
    <circle cx="142" cy="96" r="5.5" fill="url(#xh-red)" /><circle cx="182" cy="122" r="6" fill="url(#xh-gold)" />
    <circle cx="128" cy="138" r="6" fill="url(#xh-blue)" /><circle cx="176" cy="88" r="4.6" fill="url(#xh-blue)" />
    <circle cx="200" cy="140" r="5.6" fill="url(#xh-red)" /><circle cx="156" cy="128" r="5" fill="url(#xh-red)" />
    {/* twinkling lights */}
    {BULBS.map(([x, y, c]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="2.1" fill={c} />)}
    {/* the star, with its glow */}
    <circle cx="160" cy="30" r="18" fill="url(#xh-glow)" opacity=".7" />
    <path d="M160 14 L164.6 25.2 L176.6 25.8 L167.2 33.2 L170.4 45 L160 38.4 L149.6 45 L152.8 33.2 L143.4 25.8 L155.4 25.2 Z"
      fill="url(#xh-gold)" stroke="#fef3c7" strokeWidth="1" />
    {/* presents with bows */}
    <g><rect x="82" y="142" width="28" height="22" rx="2.5" fill="url(#xh-box-r)" /><rect x="94" y="142" width="4" height="22" fill="#fde68a" />
      <rect x="82" y="150" width="28" height="4" fill="#fde68a" /><ellipse cx="91" cy="140" rx="6" ry="3.5" fill="#fde68a" /><ellipse cx="101" cy="140" rx="6" ry="3.5" fill="#fde68a" /></g>
    <g><rect x="214" y="138" width="32" height="26" rx="2.5" fill="url(#xh-box-b)" /><rect x="228" y="138" width="4" height="26" fill="#f8fafc" />
      <rect x="214" y="148" width="32" height="4" fill="#f8fafc" /><ellipse cx="225" cy="136" rx="7" ry="3.6" fill="#f8fafc" /><ellipse cx="235" cy="136" rx="7" ry="3.6" fill="#f8fafc" /></g>
    <g><rect x="248" y="150" width="22" height="15" rx="2" fill="url(#xh-box-g)" /><rect x="257" y="150" width="3.6" height="15" fill="#fecaca" />
      <ellipse cx="255" cy="148.5" rx="4.6" ry="2.6" fill="#fecaca" /><ellipse cx="262" cy="148.5" rx="4.6" ry="2.6" fill="#fecaca" /></g>
  </svg>
);

/* Santa's sleigh and two reindeer, as a silhouette lit by the moon — it crosses the sky now and then */
const Sleigh = () => (
  <svg className="xmas-hero-sleigh" viewBox="0 0 160 46" aria-hidden="true" focusable="false">
    <g fill="#fde68a" opacity=".92">
      {/* reindeer */}
      {[[96, 0], [128, -4]].map(([x, dy]) => (
        <g key={x} transform={`translate(${x} ${dy})`}>
          <ellipse cx="14" cy="22" rx="11" ry="5" /><path d="M23 20 Q28 12 31 10 L33 12 Q30 16 27 22 Z" /><circle cx="32" cy="10" r="3" />
          <path d="M31 8 L29 2 M31 7 L34 1 M30 4 L27 3 M33 3 L36 3" stroke="#fde68a" strokeWidth="1.2" fill="none" />
          <path d="M6 25 L2 33 M10 26 L9 34 M19 26 L22 33 M22 25 L27 31" stroke="#fde68a" strokeWidth="1.6" strokeLinecap="round" />
        </g>
      ))}
      <path d="M58 26 L100 22" stroke="#fde68a" strokeWidth="0.9" />
      {/* the sleigh with Santa */}
      <path d="M8 22 L54 22 Q60 22 60 30 L58 34 Q52 38 20 38 Q8 38 6 30 Z" />
      <path d="M2 40 Q4 44 12 44 L60 44 Q66 44 66 38" stroke="#fde68a" strokeWidth="2" fill="none" strokeLinecap="round" />
      <circle cx="30" cy="12" r="6" /><path d="M24 12 Q30 2 38 6 L36 10 Z" /><path d="M22 22 Q26 14 34 16 L40 22 Z" />
      <path d="M38 22 L46 12 L52 22 Z" opacity=".9" />
    </g>
  </svg>
);

export default function XmasHero() {
  const { t } = useTranslation();
  const season = useSeason();
  return (
    <section className="xmas-hero" role="note" aria-label={t('xShell.xmasMenu')}>
      <span className="xmas-hero-stars" aria-hidden="true" />
      <span className="xmas-hero-moon" aria-hidden="true" />
      <span className="xmas-hero-shoot" aria-hidden="true" />
      <span className="xmas-hero-flight" aria-hidden="true"><Sleigh /></span>
      <span className="xmas-hero-snow" aria-hidden="true" />
      <div className="xmas-hero-text">
        <h2>{t('xShell.xmasHeroTitle')}</h2>
        <p>{t('xShell.xmasGreeting')}</p>
        <p className="xmas-hero-wish">{t('xShell.xmasWish')}</p>
        {/* the count, and the record beside it (Max 31/10: "cái đĩa nên ở trên banner nhé") */}
        <div className="xmas-hero-row">
          <span className="xmas-hero-count">{xmasCountdown(t)}</span>
          <XmasMusic />
        </div>
        {/* the way out, on the banner itself (Max 31/10: "tôi cần user biết chỗ tắt hiệu ứng nếu họ không muốn") — the header's tree
            button does the same and is where it comes back on */}
        <p className="xmas-hero-off">
          <span>{t('xShell.xmasOffAsk')}</span>
          <button type="button" onClick={() => season.setSnow(!season.snow)} aria-pressed={!season.snow} data-xmas-hero-motion>
            {season.snow ? t('xShell.xmasMotionOff') : t('xShell.xmasMotionOn')}
          </button>
          <button type="button" onClick={() => season.setOn(false)} data-xmas-hero-off>{t('xShell.xmasTipOff')}</button>
        </p>
      </div>
      <span className="xmas-hero-artbox"><Art /><Glints /></span>
    </section>
  );
}
